// IncomingHooksSection.jsx

import React from 'react';

// Utilities
import { cleanString } from '../../apiUtils';
import { normalizeIncomingAction } from '../utils/integrationUtils';

// Components
import Button from 'components/button/Button';

/**
 * IncomingHooksSection
 *
 * Renders incoming webhook rows and row-level actions for the integrations page.
 *
 * @param {object} theme - Active theme palette values.
 * @param {boolean} canManageCredentials - Whether the user can manage integration credentials.
 * @param {boolean} incomingLoading - Whether incoming hooks are currently loading.
 * @param {array} sortedIncomingHooks - Incoming hooks sorted for display.
 * @param {Map<string, string>} typeLabelMap - Map of type id to readable label.
 * @param {string} busyActionKey - Current busy action key used to disable actions.
 * @param {function} onAddWebhook - Handler to open create webhook flow.
 * @param {function} onCopyUrl - Handler to copy webhook url.
 * @param {function} onOpenSetup - Handler to open setup instructions for a hook.
 * @param {function} onToggleStatus - Handler to toggle a hook status.
 * @param {function} onDeletePrompt - Handler to open delete confirmation for a hook.
 *
 * @returns {ReactNode} The rendered incoming hooks section.
 */
const IncomingHooksSection = ({
    theme,
    canManageCredentials,
    incomingLoading,
    sortedIncomingHooks,
    typeLabelMap,
    busyActionKey,
    onAddWebhook,
    onCopyUrl,
    onOpenSetup,
    onToggleStatus,
    onDeletePrompt,
}) => {

  // ----- RENDER ----- //

  return (
        <section
          className="api-integrations-card"
          style={{
                backgroundColor: theme.panel,
                borderColor: theme.backgroundMuted,
            }}
        >
            <div
              className="api-integrations-title"
              style={{
                    color: theme.foreground,
                }}
            >
                Incoming Webhooks
            </div>
            <div
              className="api-integrations-subtitle"
              style={{
                    color: theme.foregroundMuted,
                }}
            >
                Receive external events and create new base records.
            </div>

            <div className="api-integrations-actions">
                <Button
                  label="+ Add Webhook"
                  iconKey="person_add"
                  enabled={canManageCredentials && !incomingLoading}
                  onClick={onAddWebhook}
                />
            </div>

            <div className="api-integrations-list">
                {sortedIncomingHooks.length === 0 && (
                    <div
                      className="api-integrations-empty">
                        {incomingLoading ? 'Loading incoming webhooks...' : 'No incoming webhooks yet.'}
                    </div>
                )}

                {sortedIncomingHooks.map((hook) => {
                    const hookId = cleanString(hook?.id || hook?.key);
                    const hookStatus = cleanString(hook?.status || 'active').toLowerCase() === 'paused' ? 'paused' : 'active';
                    const actionLabel = normalizeIncomingAction(hook?.action) === 'update' ? 'update' : 'create';
                    const typeIdKey = cleanString(hook?.typeId || hook?.typeIdLower).toLowerCase();
                    const typeLabel = typeLabelMap.get(typeIdKey) || 'Unknown type';
                    const hookUrl = cleanString(hook?.url);

                    return (
                        <div
                          className="api-integrations-row"
                          style={{
                                borderColor: theme.backgroundMuted,
                            }}
                          key={hookId}
                        >
                            <div className="api-integrations-row-meta">
                                <div
                                  className="api-integrations-row-title"
                                  style={{
                                        color: theme.foreground,
                                    }}
                                >
                                    {hook?.name || ''}
                                </div>
                                <div
                                  className="api-integrations-row-subtitle"
                                  style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    action: {actionLabel} for type {typeLabel}
                                </div>
                                <div
                                  className="api-integrations-row-subtitle"
                                  style={{
                                        color: theme.foregroundMuted,
                                    }}
                                >
                                    url: {hookUrl || 'Unavailable'}
                                </div>
                            </div>

                            <div className="api-integrations-row-actions">
                                <span
                                  className={`api-integrations-status-pill api-integrations-status-pill-${hookStatus}`}
                                  style={{
                                        borderColor: hookStatus === 'active' ? (theme.success || '#2ecc71') : theme.foregroundMuted,
                                        color: hookStatus === 'active' ? (theme.success || '#2ecc71') : theme.foregroundMuted,
                                    }}
                                >
                                    {hookStatus}
                                </span>

                                <Button
                                  label="Copy URL"
                                  enabled={Boolean(hookUrl)}
                                  height="30px"
                                  fontSize="12px"
                                  borderRadius="6px"
                                  onClick={() => onCopyUrl(hookUrl, 'Incoming webhook URL copied.')}
                                />

                                <Button
                                  label="Setup"
                                  enabled={Boolean(hook)}
                                  height="30px"
                                  fontSize="12px"
                                  borderRadius="6px"
                                  onClick={() => onOpenSetup(hook)}
                                />

                                <Button
                                  label={hookStatus === 'active' ? 'Pause' : 'Activate'}
                                  enabled={canManageCredentials && busyActionKey !== `toggle-incoming:${hookId}`}
                                  height="30px"
                                  fontSize="12px"
                                  borderRadius="6px"
                                  onClick={() => onToggleStatus(hook)}
                                />

                                <Button
                                  label="Delete"
                                  enabled={canManageCredentials && busyActionKey !== `delete-incoming:${hookId}`}
                                  height="30px"
                                  fontSize="12px"
                                  borderRadius="6px"
                                  foregroundColor={theme.warning}
                                  borderColor={theme.warning}
                                  onClick={() => onDeletePrompt(hook)}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};

// Dev Tools
IncomingHooksSection.displayName = 'IncomingHooksSection';

export default IncomingHooksSection;
